import React, { useEffect } from "react";
import { Card, CardBody, CardHeader, Row, Col, Input, Label, FormGroup, Button, Badge, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import { levelSchema, levelDefaults } from "../../../../models/level";
import PropTypes from "prop-types";
import { OverlayLoader, LoadingButton } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";;

/**
 * LevelEditor
 * Editor completo: Nome e Valor do nível.
 * O campo "order" é gerenciado pelo drag and drop e salvo automaticamente.
 */
const LevelEditor = ({ level, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: levelDefaults,
        validationSchema: levelSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando o nível selecionado muda
    useEffect(() => {
        if (level) {
            formik.resetForm({
                values: {
                    id: level.id || "",
                    name: level.name || "",
                    value: level.value || 0,
                    order: level.order || 0,
                    status: level.status || GLOBAL_STATUS.ACTIVE,
                    createdAt: level.createdAt || null,
                    createdBy: level.createdBy || null
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!level) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-stairs"></i>
                        </div>
                    </div>
                    <h5>Selecione um nível</h5>
                    <p className="text-muted">Escolha um nível na lista ao lado ou crie um novo para gerenciar.</p>
                </div>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                <i className="mdi mdi-stairs font-size-22"></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{formik.values.id ? formik.values.name : "Novo Nível"}</span>
                                {formik.values.id && (
                                    <Badge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                        {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                                    </Badge>
                                )}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">Dados do nível</p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {formik.values.id && (
                            <Button
                                color="light"
                                className="btn-md rounded-0 px-2 shadow-none text-danger border-0 d-flex align-items-center justify-content-center"
                                style={{ width: '38px', height: '38px' }}
                                onClick={() => onDelete(formik.values.id)}
                                disabled={isSaving}
                                title="Excluir Nível"
                            >
                                <i className="mdi mdi-trash-can-outline font-size-18"></i>
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={formik.handleSubmit}
                            loading={isSaving}
                            loadingText="Salvando..."
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-4">
                <OverlayLoader show={isSaving} label="Salvando..." />
                <Row>
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                            Dados do Nível
                        </h6>
                    </Col>

                    <Col md="6">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Nome do Nível</Label>
                            <Input
                                name="name"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Ex: Iniciante, Intermediário, Avançado..."
                                {...formik.getFieldProps("name")}
                                invalid={formik.touched.name && !!formik.errors.name}
                            />
                            <FormFeedback>{formik.errors.name}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Valor (Pts)</Label>
                            <Input
                                name="value"
                                type="number"
                                className="form-control bg-light-subtle border-0"
                                placeholder="0"
                                {...formik.getFieldProps("value")}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Status</Label>
                            <div className="form-check form-switch pt-2">
                                <Input
                                    type="switch"
                                    role="switch"
                                    checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                    onChange={(e) => {
                                        formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                        onChange(true);
                                    }}
                                    className="form-check-input custom-switch-primary"
                                />
                                <Label className="ms-2 small text-muted">
                                    {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                                </Label>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

LevelEditor.propTypes = {
    level: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default LevelEditor;
